import { Injectable, inject } from '@angular/core';
import { Router } from '@angular/router';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { concatLatestFrom } from '@ngrx/operators';
import { Store } from '@ngrx/store';
import {
  concatMap,
  debounceTime,
  distinctUntilChanged,
  EMPTY,
  filter,
  from,
  map,
  merge,
  of,
  switchMap,
  take,
} from 'rxjs';
import { catchError } from 'rxjs/operators';

import { DetailViewActions, NotificationActions, TeamListStateActions, TeamSelectors } from '@ninety/platform/state';

import { itemTypeToDetailType, itemTypeToPageRoute } from './global-search-api.model';
import { GlobalSearchApiService } from './global-search-api.service';
import { GlobalSearchComponentActions, GlobalSearchEffectActions } from './global-search-state.actions';
import { selectGlobalSearchQueryParameters } from './global-search-state.selectors';

@Injectable()
export class GlobalSearchEffects {
  static readonly DEBOUNCE_TIME_MS = 300;

  private actions$ = inject(Actions);
  private searchApiService = inject(GlobalSearchApiService);
  private readonly store = inject(Store);
  private readonly router = inject(Router);

  search$ = createEffect(() =>
    this.actions$.pipe(
      ofType(GlobalSearchComponentActions.searchTextChanged),
      map(({ query }) => query.trim()),
      distinctUntilChanged(),
      debounceTime(GlobalSearchEffects.DEBOUNCE_TIME_MS),
      map(query => {
        if (!query) {
          return GlobalSearchEffectActions.searchCleared();
        }

        return GlobalSearchComponentActions.beginSearch({ query });
      })
    )
  );

  beginSearch$ = createEffect(() =>
    this.actions$.pipe(
      ofType(GlobalSearchComponentActions.beginSearch),
      map(({ query }) => query.trim()),
      concatLatestFrom(() => this.store.select(selectGlobalSearchQueryParameters)),
      switchMap(([_, params]) => {
        return this.searchApiService.search(params).pipe(
          map(response => GlobalSearchEffectActions.searchSuccess({ response })),
          catchError((error: unknown) => of(GlobalSearchEffectActions.searchFailure({ error })))
        );
      })
    )
  );

  loadMoreResults$ = createEffect(() =>
    this.actions$.pipe(
      ofType(GlobalSearchComponentActions.loadMoreResults),
      concatLatestFrom(() => this.store.select(selectGlobalSearchQueryParameters)),
      concatMap(([_, params]) => {
        const request$ = this.searchApiService.search(params).pipe(
          map(response => GlobalSearchEffectActions.searchSuccess({ response })),
          catchError((error: unknown) => of(GlobalSearchEffectActions.loadMoreResultsFailure({ error })))
        );

        const cancelOnNewSearch$ = this.actions$.pipe(
          ofType(GlobalSearchComponentActions.beginSearch),
          take(1),
          map(() => null)
        );

        return merge(request$, cancelOnNewSearch$).pipe(
          take(1),
          filter((action): action is { type: string } => action != null)
        );
      })
    )
  );

  searchError$ = createEffect(() =>
    this.actions$.pipe(
      ofType(GlobalSearchEffectActions.searchFailure, GlobalSearchEffectActions.loadMoreResultsFailure),
      map(({ error }) =>
        NotificationActions.notifyError({
          message: error instanceof Error ? error.message : 'An error occurred while searching.',
          error: error,
        })
      )
    )
  );

  /**
   * When a search result is selected, navigate to the relevant page, set the team, and open the detail view.
   *
   * Flow:
   * 1. Navigate to the page if not already there (router.navigate returns null if already on route)
   * 2. Change the team if not already selected (wait for getSelectedTeamSuccess if team changes)
   * 3. Always open the detail view to the item
   */
  openSearchResultItem$ = createEffect(() =>
    this.actions$.pipe(
      ofType(GlobalSearchEffectActions.openSearchResultItem),
      concatLatestFrom(() => this.store.select(TeamSelectors.selectFilterBarTeamId)),
      switchMap(([{ item }, currentTeamId]) => {
        const pageRoute = itemTypeToPageRoute(item);
        const detailType = itemTypeToDetailType[item.itemType];

        if (!pageRoute) return of(GlobalSearchEffectActions.unknownSearchResultItemType({ item }));

        const openDetailProps = { detailType, itemId: item._id };

        const needsTeamChange = item.teamId && currentTeamId !== item.teamId;

        const resultAction = needsTeamChange
          ? GlobalSearchEffectActions.changeTeamAndOpenDetailPane({
              teamId: item.teamId,
              ...openDetailProps,
            })
          : GlobalSearchEffectActions.openDetailPane(openDetailProps);

        /** If already on route, skip navigation */
        const isAlreadyOnRoute = this.router.url.includes(pageRoute);
        if (isAlreadyOnRoute) return of(resultAction);

        /** Navigate to the route first */
        return from(this.router.navigate([pageRoute])).pipe(
          concatMap(success => {
            if (success === false) return EMPTY;
            return of(resultAction);
          })
        );
      })
    )
  );

  changeTeamAndOpenDetails$ = createEffect(() =>
    this.actions$.pipe(
      ofType(GlobalSearchEffectActions.changeTeamAndOpenDetailPane),
      switchMap(({ teamId, detailType, itemId }) => {
        // On team change success, dispatch openDetailPane action regardless of which team is selected
        const completion$ = this.actions$.pipe(
          ofType(TeamListStateActions.getSelectedTeamSuccess),
          take(1),
          map(() => GlobalSearchEffectActions.openDetailPane({ detailType, itemId }))
        );

        return merge(completion$, of(TeamListStateActions.changeFilterBarTeam({ id: teamId })));
      })
    )
  );

  openDetailPane$ = createEffect(() =>
    this.actions$.pipe(
      ofType(GlobalSearchEffectActions.openDetailPane),
      map(({ detailType, itemId }) =>
        DetailViewActions.opened({
          config: { pathParts: [detailType, itemId] },
          source: 'Search Result Item Selected',
        })
      )
    )
  );
}
